/**
 * @file websiteService.ts
 * @description Service for handling website-related API calls.
 */

import config from "../config/config";
import { auth } from "../config/firebaseConfig";
import log from "../config/logging";
import { Website } from "../types";

export interface CreateWebsiteData {
  clientId: string;
  website: string;
  contentType: string;
  wpUsername: string;
  wpAppPassword: string;
  wpPostType?: string;
  wpStatus?: string;
  wpCategories?: number[];
}

export interface UpdateWebsiteData extends Partial<CreateWebsiteData> {}

/**
 * Get all websites for the current user, optionally filtered by clientId.
 * @param clientId - Optional client ID to filter websites
 * @returns Array of websites
 */
export const getWebsites = async (
  clientId?: string
): Promise<Website[]> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const url = new URL(`${config.backendUrl}/api/websites`);
    if (clientId) {
      url.searchParams.append("clientId", clientId);
    }

    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to fetch websites: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.websites || [];
  } catch (error) {
    log.error("Error fetching websites", { error });
    throw error;
  }
};

/**
 * Create a new website.
 * @param websiteData - Website data including WordPress configuration
 * @returns The created website ID
 */
export const createWebsite = async (
  websiteData: CreateWebsiteData
): Promise<string> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = await fetch(`${config.backendUrl}/api/websites`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(websiteData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to create website: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.websiteId;
  } catch (error) {
    log.error("Error creating website", { error });
    throw error;
  }
};

/**
 * Update an existing website.
 * @param websiteId - ID of the website to update
 * @param websiteData - Updated website data
 */
export const updateWebsite = async (
  websiteId: string,
  websiteData: UpdateWebsiteData
): Promise<void> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = await fetch(
      `${config.backendUrl}/api/websites/${websiteId}`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(websiteData),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to update website: ${response.statusText}`
      );
    }
  } catch (error) {
    log.error("Error updating website", { error });
    throw error;
  }
};

/**
 * Delete a website.
 * @param websiteId - ID of the website to delete
 */
export const deleteWebsite = async (websiteId: string): Promise<void> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = await fetch(
      `${config.backendUrl}/api/websites/${websiteId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to delete website: ${response.statusText}`
      );
    }
  } catch (error) {
    log.error("Error deleting website", { error });
    throw error;
  }
};
